import { useRef, useState } from 'react';
import type { ReactNode, UIEvent } from 'react';
import { ArrowLeft, Menu } from 'lucide-react';
import { IconButton, LEGACY_CARD_SHADOW, MoreMenu } from '@components/rep';
import type { MoreMenuItem } from '@components/rep';
import { useAppDispatch } from '@store/hooks';
import { SET_NAV_OPEN } from '@store/app/app.reducer';
import { cn } from '@utils/index';

/**
 * The header buttons (Figma 2782:3977): a 36px square with 8px corners, translucent white on the
 * blue band, around a 16px icon in ui/icon/accent-subtle.
 */
const HEADER_BUTTON_CLASS =
  'rep:size-9! rep:rounded-lg! rep:bg-white/8! rep:hover:bg-white/16! rep:[&_svg]:size-4! rep:[&_svg]:text-[#F0F3FF]!';

interface ListPageShellProps {
  title: string;
  /** The line under the title, e.g. the row count. */
  subtitle?: string;
  /** The header's menu, top right; none when empty. */
  menuItems?: MoreMenuItem[];
  /**
   * A Back arrow in place of the navigation button, for a list opened from another list (a
   * county's letters, opened from the county list).
   */
  onBack?: () => void;
  /** Phone gutters (16px) in place of the desktop ones (24px). */
  isMobile?: boolean;
  /**
   * Keeps the button row on top while the page scrolls: once the big title has scrolled under
   * it, the row turns solid blue and shows the title small.
   */
  collapsible?: boolean;
  /** The white card's content: the toolbar and the table. */
  children: ReactNode;
}

/**
 * List page chrome in the Palmetto 3.0 layout (Figma Virtual EOC 2782:3971), the list-page
 * sibling of EntryPageShell: the blue header (menu button, title and subtitle, optional menu)
 * and the white card that overlaps its bottom 47px, on the page's grey. The whole page scrolls,
 * so the header goes away with the content; `collapsible` keeps its button row on top as a
 * small blue bar.
 *
 * The button row and the band are siblings (the band slides up under the row by the row's
 * 68px), and the <header> around them is display: contents, so a sticky row is held by the
 * scroll container rather than by the header, which scrolls away.
 */
const ListPageShell = ({
  title,
  subtitle,
  menuItems = [],
  onBack,
  isMobile,
  collapsible,
  children,
}: ListPageShellProps) => {
  const dispatch = useAppDispatch();
  const gutter = isMobile ? 'rep:px-4' : 'rep:px-6';
  const barRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [stuck, setStuck] = useState(false);

  // Stuck once the big title is fully under the row. At the top nothing is stuck, which also
  // covers a page kept alive hidden, whose zero-size boxes would otherwise read as stuck.
  const handleScroll = collapsible
    ? (e: UIEvent<HTMLDivElement>) => {
        const bar = barRef.current?.getBoundingClientRect();
        const heading = titleRef.current?.getBoundingClientRect();
        if (!bar || !heading) return;
        setStuck(e.currentTarget.scrollTop > 0 && heading.bottom <= bar.bottom);
      }
    : undefined;

  return (
    <div
      onScroll={handleScroll}
      className='rep:h-full rep:w-full rep:overflow-y-auto rep:bg-[#ECEEF2]'>
      <header className='rep:contents'>
        <div
          ref={barRef}
          className={cn(
            'rep:z-10 rep:flex rep:h-17 rep:w-full rep:items-center rep:justify-between rep:gap-2 rep:transition-[background-color,box-shadow] rep:duration-200 rep:motion-reduce:transition-none',
            collapsible ? 'rep:sticky rep:top-0' : 'rep:relative',
            stuck &&
              'rep:bg-[#0949EE] rep:shadow-[0_1px_0_rgb(2_6_24/0.06),0_6px_16px_-6px_rgb(2_6_24/0.35)]',
            gutter,
          )}>
          {onBack ? (
            <IconButton aria-label='Back' onClick={onBack} className={HEADER_BUTTON_CLASS}>
              <ArrowLeft className='rep:size-4' />
            </IconButton>
          ) : (
            <IconButton
              aria-label='Open navigation'
              onClick={() => dispatch(SET_NAV_OPEN(true))}
              className={HEADER_BUTTON_CLASS}>
              <Menu className='rep:size-4' />
            </IconButton>
          )}
          {collapsible ? (
            // The h1 below stays the page's heading; this copy is only what the eye reads.
            <div
              aria-hidden
              className={cn(
                'rep:min-w-0 rep:flex-1 rep:truncate rep:pl-2 rep:text-lg rep:leading-6 rep:font-medium rep:text-[#FEFEFE] rep:transition-[opacity,translate] rep:duration-200 rep:motion-reduce:transition-none',
                stuck ? 'rep:translate-y-0 rep:opacity-100' : 'rep:translate-y-1.5 rep:opacity-0',
              )}>
              {title}
            </div>
          ) : null}
          <MoreMenu items={menuItems} buttonClassName={HEADER_BUTTON_CLASS} />
        </div>
        <div
          className={cn(
            'rep:-mt-17 rep:flex rep:flex-col rep:items-center rep:bg-[#0949EE] rep:bg-linear-to-b rep:from-transparent rep:to-black/20 rep:pt-23 rep:pb-18',
            gutter,
          )}>
          <div className='rep:flex rep:w-full rep:max-w-7xl rep:flex-col rep:gap-2'>
            <h1
              ref={titleRef}
              className='rep:text-[36px] rep:leading-[1.3] rep:font-medium rep:text-[#FEFEFE]'>
              {title}
            </h1>
            {subtitle !== undefined ? (
              <div className='rep:min-h-[1.3em] rep:text-base rep:leading-[1.3] rep:text-[#ECEEF2]'>
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
      </header>
      {/* Bottom room for the page's FAB, so it never covers the card's last line. */}
      <div className={cn('rep:pb-28', gutter)}>
        <section
          className='rep:relative rep:mx-auto rep:-mt-11.75 rep:flex rep:w-full rep:max-w-7xl rep:flex-col rep:gap-4 rep:rounded-[14px] rep:bg-white rep:p-4'
          style={{ boxShadow: LEGACY_CARD_SHADOW }}>
          {children}
        </section>
      </div>
    </div>
  );
};

export default ListPageShell;
